import { skipToken } from '@apollo/client/react'
import { getDefaultStore, useAtom, useSetAtom } from 'jotai'
import { atomWithStorage, RESET } from 'jotai/utils'
import type { CSSProperties } from 'react'
import { match } from 'ts-pattern'
import { useMyInfo } from 'src/services/apollo/auth'
import type { PriceWithCurrency } from 'src/services/apollo/generated/graphql'
import { Interval } from 'src/services/apollo/generated/graphql'
import { useGetAllPaymentItems } from 'src/services/apollo/payment'
import type { DynamicConstants } from 'src/services/constants'
import { usePublicDynamicConfig } from 'src/services/restful/dynamicConfig'
import dayjs from 'src/utils/dayjs'

const defaultStore = getDefaultStore()

export const membershipCommonOfferCode = 'commonOffer'
// NOTE: recallOffer has been removed from config
// Keeping it here for reference only
export const membershipRecallOfferCode = 'recallOffer'

const DISCOUNT_VALID_BEFORE_KEY = 'growth:new-member-discount-valid-before'
const discountValidBeforeAtom = atomWithStorage<number | undefined>(
  DISCOUNT_VALID_BEFORE_KEY,
  undefined,
)
/**
 * Hooks for storing discount available before
 * Unit is milliseconds
 * @returns
 */
export function useMembershipDiscountValidBefore() {
  return useAtom(discountValidBeforeAtom, { store: defaultStore })
}
export function useSetMembershipDiscountValidBefore() {
  return useSetAtom(discountValidBeforeAtom, { store: defaultStore })
}

function getNewTargetTime() {
  return dayjs().add(10, 'minute').subtract(1, 'seconds').valueOf()
}

export function useMembershipDiscountValidCountdownUpdate() {
  const setBefore = useSetMembershipDiscountValidBefore()

  return (args?: { forceUpdate?: boolean }) =>
    setBefore(prev => {
      if (!prev || args?.forceUpdate) {
        return getNewTargetTime()
      }
      if (prev - Date.now() > 0) {
        // Still have time, do nothing
        return prev
      }
      return getNewTargetTime()
    })
}
export function useMembershipDiscountValidCountdownReset() {
  const setBefore = useSetMembershipDiscountValidBefore()
  return setBefore(RESET)
}

// TODO: membership discount part 2
// export function useUserNewMemberOffer() {
//   const { data: userInfo } = useMyInfo()
//   const [beforeDate] = useMembershipDiscountValidBefore()
//   const userEligiblePercentage = userInfo?.newMemberOffer

//   const stillHaveTime = beforeDate && beforeDate - new Date().getTime() > 0
//   return stillHaveTime ? userEligiblePercentage : undefined
// }

export function getMembershipDiscountValidBefore() {
  return window?.localStorage?.getItem(DISCOUNT_VALID_BEFORE_KEY)
}

export function clearMembershipDiscountValidBefore() {
  const curVal = getMembershipDiscountValidBefore()
  if (!curVal) return
  return window?.localStorage?.removeItem(DISCOUNT_VALID_BEFORE_KEY)
}

/**
 * For MONTH only
 * Convert { membership-start-MONTH: Currency } to { membership-start: Currency }
 * @see packages/pixai-app/src/services/constants.ts
 * @param param0
 */
function getPricesForMonthlyPlans(
  discounts: Record<string, { currencies: PriceWithCurrency[] }>,
): Record<string, { currencies: PriceWithCurrency[] }> {
  const entries = Object.entries(discounts).map(([planId, item]) => [
    planId.replace(`-${Interval.Month}`, ''),
    item,
  ])
  return Object.fromEntries(entries)
}

export function useMembershipMonthlyDiscountedPrices(
  type: 'common' | 'recall' | undefined,
) {
  // Skip fetching unless eligible
  const { data: dynamicConstants } = usePublicDynamicConfig(
    type ? 'constants' : undefined,
  )
  if (!type) return undefined
  if (!dynamicConstants) return undefined
  const key = membershipCommonOfferCode
  const prices = (dynamicConstants as DynamicConstants | undefined)?.[key]
    ?.prices
  if (!prices) return
  return getPricesForMonthlyPlans(prices)
}

/**
 * Common Discount: A.k.a One Time Discount
 * Recall Discount: Should discount when user has canceled a payment
 * Monthly only
 * @returns
 * @deprecated
 */
export function useUserIsEligibleForDiscount() {
  const { data: userInfo } = useMyInfo()
  const { data: paymentItems, ...rest } = useGetAllPaymentItems(
    userInfo ? {} : skipToken,
  )
  const prices = paymentItems?.map(it => it.prices).flat()
  const commonAvailable = prices?.some(
    it => it?.promotionalCode === membershipCommonOfferCode,
  )
  const recallAvailable = prices?.some(
    it => it?.promotionalCode === membershipRecallOfferCode,
  )
  return {
    data: {
      /** Should not enable recall discount if common discount is available */
      commonAvailable: commonAvailable,
      recallAvailable: recallAvailable,
    },
    ...rest,
  }
}

/**
 * @deprecated
 */
export function useDiscountBannerConditions() {
  const {
    data: { commonAvailable, recallAvailable },
  } = useUserIsEligibleForDiscount()
  const [before] = useMembershipDiscountValidBefore()

  if (commonAvailable) return 'common'
  if (recallAvailable && before && before > Date.now()) return 'recall'
  return undefined
}

const membershipDiscountCommonStyles = {
  '--accent-color': '#FDC729',
  '--accent-color-dark': '#EEBB26',
  '--accent-color-text': '#665200',
  '--accent-color-text-dark': '#665200',
  '--accent-color-highlight': '#662500',
  '--accent-color-highlight-dark': '#F3BF27',
} as CSSProperties

export function getMembershipDiscountStylesByTier(tier: number): CSSProperties {
  const tierColors = match(tier)
    .returnType<{
      '--plan-bg-color': string
      '--plan-bg-color-dark': string
    }>()
    .with(1, () => ({
      '--plan-bg-color': '#0000',
      '--plan-bg-color-dark': '#0000',
    }))
    .with(2, () => ({
      '--plan-bg-color': '#FFFAE7',
      '--plan-bg-color-dark': '#FDC7293F',
    }))
    .with(3, () => ({
      '--plan-bg-color': '#e2e8f0',
      '--plan-bg-color-dark': '#374151',
    }))
    .otherwise(() => ({
      '--plan-bg-color': '#FFF',
      '--plan-bg-color-dark': '#000',
    }))
  return {
    ...membershipDiscountCommonStyles,
    ...tierColors,
  }
}

export function getDiscountTypeForTracking(discount?: string) {
  // Update event names if new discount types are added
  return match(discount)
    .with('common', () => 'new-user-2406')
    .with('recall', () => 'recall-2406')
    .otherwise(() => undefined)
}
